import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, ShoppingCart, Trash2, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, fmtDateTime } from "@/lib/format";
import { shoppingListText, sendToWhatsApp } from "@/lib/shoppingList";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { Button, EmptyState, ErrorState, Loading, Page, PageHeader } from "@/components/kit";

function ListCard({ l, onDelete, onDuplicate, unitName, showCost }) {
  const nav = useNavigate();
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div className="card cursor-pointer flex flex-col gap-3" onClick={() => nav(`/shopping-lists/${l.id}`)} data-testid={`shopping-list-card-${l.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><ShoppingCart size={18} style={{ color: "var(--brand)" }} /></div>
        <div className="flex-1 min-w-0">
          <div className="h3 truncate">{l.name}</div>
          <div className="caption truncate">{l.items_count} ite{l.items_count === 1 ? "m" : "ns"}{showCost ? ` · estimado ${brl(l.estimated_total)}` : ""}</div>
        </div>
      </div>
      {l.note && <p className="caption line-clamp-2">{l.note}</p>}
      <div className="caption">Atualizada por {l.updated_by} · {fmtDateTime(l.updated_at)}</div>
      <div className="flex gap-2 mt-auto">
        <Button size="sm" icon={MessageCircle} disabled={!l.items_count} onClick={stop(() => sendToWhatsApp(shoppingListText(l, unitName)))} data-testid={`shopping-list-whatsapp-${l.id}`}>WhatsApp</Button>
        <button className="icon-btn" title="Duplicar" onClick={stop(onDuplicate)} data-testid={`shopping-list-duplicate-${l.id}`}><Copy size={15} /></button>
        <button className="icon-btn ml-auto" title="Excluir" onClick={stop(() => window.confirm(`Excluir a lista "${l.name}"?`) && onDelete())} data-testid={`shopping-list-delete-${l.id}`}><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function ShoppingLists() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { isMgmt } = useAuth();
  const { unit } = useUnit();
  const { data = [], isLoading, isError, error, refetch } = useQuery({ queryKey: ["shopping-lists"], queryFn: () => api("/shopping-lists") });
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["shopping-lists"] }); toast.success(msg); };
  const del = useMutation({ mutationFn: (id) => api(`/shopping-lists/${id}`, { method: "DELETE" }), onSuccess: () => done("Lista excluída"), onError: (e) => toast.error(e.message) });
  const dup = useMutation({ mutationFn: (id) => api(`/shopping-lists/${id}/duplicate`, { method: "POST" }), onSuccess: () => done("Lista duplicada"), onError: (e) => toast.error(e.message) });
  return (
    <Page>
      <PageHeader title="Listas de compras" back="/menu" subtitle="Monte listas com os produtos cadastrados e envie pelo WhatsApp"
        right={<Button icon={Plus} onClick={() => nav("/shopping-lists/new")} data-testid="new-shopping-list-button"><span className="hidden sm:inline">Nova lista</span></Button>} />
      {isLoading ? <Loading /> : isError ? <ErrorState message={error.message} onRetry={refetch} /> : !data.length ? (
        <EmptyState title="Nenhuma lista ainda" text="Crie listas como “Compras semanais” ou “Reposição de emergência”."
          action={<Button icon={Plus} onClick={() => nav("/shopping-lists/new")} data-testid="empty-new-shopping-list-button">Criar lista</Button>} />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="shopping-lists-grid">
          {data.map((l) => <ListCard key={l.id} l={l} unitName={unit?.name} showCost={isMgmt} onDelete={() => del.mutate(l.id)} onDuplicate={() => dup.mutate(l.id)} />)}
        </div>
      )}
    </Page>
  );
}
